import { isToday, isTomorrow, isYesterday, format, parseISO } from "date-fns"
import { de, enUS } from "date-fns/locale"

export const getDateLocale = (locale: string) => {
  return locale === 'de' ? de : enUS
}

export const getRelativeDateKey = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null
  
  // Handle legacy string dates - these are the internal keys
  if (dateStr === "Today") return "today"
  if (dateStr === "Tomorrow") return "tomorrow" 
  if (dateStr === "Yesterday") return "yesterday"
  
  // Try to parse as ISO date and determine relative key
  try {
    const date = parseISO(dateStr)
    
    if (isNaN(date.getTime())) {
      return null // Invalid date
    }
    
    if (isToday(date)) return "today"
    if (isTomorrow(date)) return "tomorrow"
    if (isYesterday(date)) return "yesterday"
    
    return null // Not a relative date
  } catch (error) {
    console.error("Error parsing date:", error)
    return null
  }
}

export const getFormattedDate = (dateStr: string | null | undefined, locale: string): string | null => {
  if (!dateStr) return null
  
  const dateLocale = getDateLocale(locale)
  
  // Handle legacy string dates - keep for internal logic
  if (dateStr === "Today" || dateStr === "Tomorrow" || dateStr === "Yesterday") {
    return dateStr // Return as-is for internal logic
  }
  
  // Try to parse as ISO date
  try {
    const date = parseISO(dateStr)
    
    if (isNaN(date.getTime())) {
      return dateStr // Return original string if invalid
    }
    
    // Format as readable date
    return format(date, "MMM d, yyyy", { locale: dateLocale })
  } catch (error) {
    console.error("Error parsing date:", error)
    return dateStr // Return original if parsing fails
  }
}

export const getDisplayDate = (dateStr: string | null | undefined, t: (key: string) => string, locale: string): string | null => {
  if (!dateStr) return null
  
  // Check if it's a relative date that should be translated
  const relativeKey = getRelativeDateKey(dateStr)
  if (relativeKey) {
    return t(relativeKey)
  }
  
  // Otherwise format as regular date
  return getFormattedDate(dateStr, locale)
}

function padDatePart(value: number): string {
  return value.toString().padStart(2, "0")
}

export function toLocalIsoDate(value: Date): string {
  return [
    value.getFullYear(),
    padDatePart(value.getMonth() + 1),
    padDatePart(value.getDate()),
  ].join("-")
}

export function toLocalDateTimeInputValue(value: string | null | undefined): string {
  if (!value) return ""

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ""

  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-") + `T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`
}

export function fromLocalDateTimeInputValue(value: string | null | undefined): string | null {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return date.toISOString()
}

export function formatDateTime(value: string | null | undefined, locale: string): string | null {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date)
}

export function isOverdueDateTime(value: string | null | undefined): boolean {
  if (!value) return false

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return false

  return date.getTime() < Date.now()
}
